/* eslint-disable indent */
/**
 * API Client Utility
 *
 * Provides a unified way to make API calls with error handling, caching, and performance optimization.
 */

import config from "../../../config/config";

// Simple in-memory cache for API responses
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Default API configuration (browser-friendly)
const defaultConfig = {
  baseUrl: config.api.baseUrl,
  timeout: config.api.timeout, // 30 seconds
  retries: config.api.retries, // Number of retry attempts
  apiKey: null
};

/**
 * Generate cache key for request
 * @param {string} url Request URL
 * @param {Object} options Request options
 * @returns {string} Cache key
 */
function generateCacheKey(url, options) {
  const method = options.method || "GET";
  const body = options.body ? JSON.stringify(options.body) : "";
  return `${method}:${url}:${body}`;
}

/**
 * Get cached response if available and not expired
 * @param {string} cacheKey Cache key
 * @returns {Object|null} Cached response or null
 */
function getCachedResponse(cacheKey) {
  const cached = cache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  if (cached) {
    cache.delete(cacheKey);
  }
  return null;
}

/**
 * Cache response
 * @param {string} cacheKey Cache key
 * @param {Object} data Response data
 */
function setCachedResponse(cacheKey, data) {
  cache.set(cacheKey, {
    data,
    timestamp: Date.now()
  });
  
  // Clean up old cache entries (keep max 50 entries)
  if (cache.size > 50) {
    const firstKey = cache.keys().next().value;
    cache.delete(firstKey);
  }
}

/**
 * Make an API request with caching
 * @param {string} endpoint The API endpoint (without base URL)
 * @param {Object} options Request options
 * @returns {Promise} Promise resolving to response data
 */
export async function apiRequest(endpoint, options = {}) {
  const apiConfig = { ...defaultConfig, ...options.config };

  // Build URL
  const url = endpoint.startsWith("http")
    ? endpoint
    : `${apiConfig.baseUrl}${
        endpoint.startsWith("/") ? endpoint : `/${endpoint}`
      }`;

  // Check cache for GET requests only
  const method = options.method || "GET";
  const enableCaching = method === "GET" && !options.noCache;
  let cacheKey;
  
  if (enableCaching) {
    cacheKey = generateCacheKey(url, options);
    const cachedResponse = getCachedResponse(cacheKey);
    if (cachedResponse) {
      return cachedResponse;
    }
  }

  // Create request options
  const requestOptions = {
    method,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "x-api-key": apiConfig.apiKey,
      ...options.headers,
    },
    ...(options.body && { body: JSON.stringify(options.body) }),
    signal: options.abortSignal,
  };

  // Set up timeout handler
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), apiConfig.timeout);

  // If no abort signal was provided, use our timeout abort signal
  if (!options.abortSignal) {
    requestOptions.signal = controller.signal;
  }

  try {
    // Make the request
    const response = await fetchWithRetries(
      url,
      requestOptions,
      config.retries
    );

    // Parse JSON
    const contentType = response.headers.get("content-type");
    let data;

    if (contentType && contentType.includes("application/json")) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    // Handle error responses
    if (!response.ok) {
      const error = new Error(data.message || "API request failed");
      error.status = response.status;
      error.data = data;
      throw error;
    }

    // Cache successful GET responses
    if (enableCaching && cacheKey) {
      setCachedResponse(cacheKey, data);
    }

    return data;
  } catch (error) {
    // Handle abort errors
    if (error.name === "AbortError") {
      throw new Error(`Request timeout after ${apiConfig.timeout}ms`);
    }

    // Re-throw other errors
    throw error;
  } finally {
    // Clear timeout
    clearTimeout(timeoutId);
  }
}

/**
 * Fetch with retry logic
 * @param {string} url The URL to fetch
 * @param {Object} options Fetch options
 * @param {number} retriesLeft Number of retries left
 * @returns {Promise} Promise resolving to fetch response
 */
async function fetchWithRetries(url, options, retriesLeft) {
  try {
    return await fetch(url, options);
  } catch (error) {
    // If no retries left or not a retriable error, throw
    if (retriesLeft <= 0 || error.name === "AbortError") {
      throw error;
    }

    // Wait before retrying (exponential backoff)
    const delay = Math.pow(2, defaultConfig.retries - retriesLeft) * 1000;
    await new Promise((resolve) => setTimeout(resolve, delay));

    // Retry
    return fetchWithRetries(url, options, retriesLeft - 1);
  }
}

/**
 * GET request
 * @param {string} endpoint The API endpoint
 * @param {Object} options Request options
 * @returns {Promise} Promise resolving to response data
 */
export function get(endpoint, options = {}) {
  return apiRequest(endpoint, {
    ...options,
    method: "GET",
  });
}

/**
 * POST request
 * @param {string} endpoint The API endpoint
 * @param {Object} data Request data
 * @param {Object} options Request options
 * @returns {Promise} Promise resolving to response data
 */
export function post(endpoint, data = {}, options = {}) {
  return apiRequest(endpoint, {
    ...options,
    method: "POST",
    body: data,
  });
}

/**
 * PUT request
 * @param {string} endpoint The API endpoint
 * @param {Object} data Request data
 * @param {Object} options Request options
 * @returns {Promise} Promise resolving to response data
 */
export function put(endpoint, data = {}, options = {}) {
  return apiRequest(endpoint, {
    ...options,
    method: "PUT",
    body: data,
  });
}

/**
 * DELETE request
 * @param {string} endpoint The API endpoint
 * @param {Object} options Request options
 * @returns {Promise} Promise resolving to response data
 */
export function del(endpoint, options = {}) {
  return apiRequest(endpoint, {
    ...options,
    method: "DELETE",
  });
}

/**
 * PATCH request
 * @param {string} endpoint The API endpoint
 * @param {Object} data Request data
 * @param {Object} options Request options
 * @returns {Promise} Promise resolving to response data
 */
export function patch(endpoint, data = {}, options = {}) {
  return apiRequest(endpoint, {
    ...options,
    method: "PATCH",
    body: data,
  });
}
